<script lang="ts">
  // Управление пользователями — только для админа.
  // Пароли здесь не хранятся и не показываются повторно: сервер отдаёт
  // сгенерированный пароль ровно один раз, дальше его знает только владелец.
  import { onMount } from 'svelte';
  import {
    createUser,
    deleteUser,
    listNvrs,
    listStreams,
    listUsers,
    resetUserPassword,
    rotateSecret,
    setUserAccess,
  } from '../lib/api';
  import type { NvrInfo, StreamInfo, UserInfo } from '../lib/types';

  type Props = { me: string; onclose: () => void; onloggedout: () => void };
  let { me, onclose, onloggedout }: Props = $props();

  let users = $state<UserInfo[]>([]);
  let nvrs = $state<NvrInfo[]>([]);
  let streams = $state<StreamInfo[]>([]);
  let loading = $state(true);
  let err = $state('');
  let busy = $state(false);

  let newName = $state('');
  let newRole = $state('viewer');
  /** Показывается один раз после создания/сброса — потом восстановить нельзя. */
  let issued = $state<{ name: string; password: string } | null>(null);

  /** Кого сейчас редактируем по доступам + черновик выбора. */
  let editing = $state<string | null>(null);
  let draftAll = $state(true);
  let draftNvrs = $state<Set<string>>(new Set());
  let draftStreams = $state<Set<string>>(new Set());
  let expanded = $state<Set<string>>(new Set());

  async function reload(): Promise<void> {
    loading = true;
    try {
      [users, nvrs, streams] = await Promise.all([listUsers(), listNvrs(), listStreams()]);
      err = '';
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  onMount(reload);

  function camsOf(nvrId: string): StreamInfo[] {
    return streams.filter((s) => s.nvr_id === nvrId);
  }

  function openAccess(u: UserInfo): void {
    editing = u.name;
    draftAll = u.nvrs.length === 0 && u.streams.length === 0;
    draftNvrs = new Set(u.nvrs);
    draftStreams = new Set(u.streams);
    expanded = new Set();
  }

  function toggle(set: Set<string>, key: string): Set<string> {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  }

  async function saveAccess(): Promise<void> {
    if (!editing) return;
    err = '';
    busy = true;
    try {
      const nv = draftAll ? [] : [...draftNvrs];
      // Камеры, уже покрытые выбранным регистратором, отдельно не пишем —
      // иначе список замусоривается и его невозможно читать.
      const st = draftAll
        ? []
        : [...draftStreams].filter((name) => {
            const s = streams.find((x) => x.name === name);
            return s ? !draftNvrs.has(s.nvr_id) : false;
          });
      if (!draftAll && nv.length === 0 && st.length === 0) {
        err = 'Выберите хотя бы одну камеру — иначе пользователь не увидит ничего';
        busy = false;
        return;
      }
      await setUserAccess(editing, nv, st);
      editing = null;
      await reload();
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
    } finally {
      busy = false;
    }
  }

  function accessLabel(u: UserInfo): string {
    if (u.role === 'admin') return 'все камеры (админ)';
    if (u.nvrs.length === 0 && u.streams.length === 0) return 'все камеры';
    const parts: string[] = [];
    for (const id of u.nvrs) parts.push(nvrs.find((n) => n.id === id)?.name ?? id);
    if (u.streams.length) parts.push(`+${u.streams.length} камер`);
    return parts.join(', ');
  }

  async function add(e: Event): Promise<void> {
    e.preventDefault();
    err = '';
    busy = true;
    try {
      const r = await createUser(newName.trim(), newRole);
      if (r.password) issued = { name: r.name, password: r.password };
      newName = '';
      await reload();
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
    } finally {
      busy = false;
    }
  }

  async function reset(name: string): Promise<void> {
    if (!confirm(`Сбросить пароль пользователю «${name}»?\nЕго текущие сессии завершатся.`)) return;
    err = '';
    busy = true;
    try {
      const r = await resetUserPassword(name);
      issued = { name, password: r.password };
      await reload();
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
    } finally {
      busy = false;
    }
  }

  async function remove(name: string): Promise<void> {
    if (!confirm(`Удалить пользователя «${name}»?`)) return;
    err = '';
    busy = true;
    try {
      await deleteUser(name);
      await reload();
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
    } finally {
      busy = false;
    }
  }

  async function killAllSessions(): Promise<void> {
    if (
      !confirm('Сменить ключ подписи?\nВсе входы на всех устройствах, включая ваш, будут завершены.')
    )
      return;
    busy = true;
    try {
      await rotateSecret();
      onloggedout();
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
      busy = false;
    }
  }

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      /* буфер недоступен — пароль виден на экране, скопируют руками */
    }
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') onclose();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="bg" onclick={(e) => { if (e.target === e.currentTarget) onclose(); }} role="presentation">
  <div class="modal">
    <h2>Пользователи</h2>

    {#if issued}
      <div class="issued">
        <div class="issued-head">Пароль для «{issued.name}»</div>
        <code>{issued.password}</code>
        <div class="issued-note">
          Показывается один раз — сохраните и передайте владельцу. При первом входе он
          должен будет задать свой пароль.
        </div>
        <div class="issued-actions">
          <button type="button" class="ghost" onclick={() => copy(issued!.password)}>Копировать</button>
          <button type="button" class="ghost" onclick={() => (issued = null)}>Понятно</button>
        </div>
      </div>
    {/if}

    {#if loading}
      <p class="muted">Загружаю…</p>
    {:else}
      <div class="list">
        {#each users as u (u.name)}
          <div class="row">
            <div class="who">
              <span class="name">{u.name}</span>
              <span class="role" class:admin={u.role === 'admin'}>
                {u.role === 'admin' ? 'администратор' : 'просмотр'}
              </span>
              <span class="access">{accessLabel(u)}</span>
              {#if u.must_change}<span class="flag">пароль не задан</span>{/if}
              {#if u.name === me}<span class="flag me">это вы</span>{/if}
            </div>
            <div class="row-actions">
              {#if u.role !== 'admin'}
                <button type="button" class="ghost" disabled={busy} onclick={() => openAccess(u)}>
                  Доступ
                </button>
              {/if}
              <button type="button" class="ghost" disabled={busy} onclick={() => reset(u.name)}>
                Сбросить пароль
              </button>
              {#if u.name !== me}
                <button type="button" class="ghost danger" disabled={busy} onclick={() => remove(u.name)}>
                  Удалить
                </button>
              {/if}
            </div>
          </div>

          {#if editing === u.name}
            <div class="access-editor">
              <label class="check strong">
                <input type="checkbox" bind:checked={draftAll} />
                <span>Все камеры</span>
              </label>

              {#if !draftAll}
                {#each nvrs as n (n.id)}
                  <div class="nvr-block">
                    <div class="nvr-head">
                      <label class="check">
                        <input
                          type="checkbox"
                          checked={draftNvrs.has(n.id)}
                          onchange={() => (draftNvrs = toggle(draftNvrs, n.id))}
                        />
                        <span>{n.name} <em>({camsOf(n.id).length})</em></span>
                      </label>
                      <button
                        type="button"
                        class="link"
                        onclick={() => (expanded = toggle(expanded, n.id))}
                      >
                        {expanded.has(n.id) ? 'свернуть' : 'по камерам'}
                      </button>
                    </div>
                    {#if expanded.has(n.id)}
                      <div class="cams">
                        {#each camsOf(n.id) as c (c.name)}
                          <label class="check cam" class:covered={draftNvrs.has(n.id)}>
                            <input
                              type="checkbox"
                              disabled={draftNvrs.has(n.id)}
                              checked={draftNvrs.has(n.id) || draftStreams.has(c.name)}
                              onchange={() => (draftStreams = toggle(draftStreams, c.name))}
                            />
                            <span>{c.label || c.name}</span>
                          </label>
                        {/each}
                      </div>
                    {/if}
                  </div>
                {/each}
              {/if}

              <div class="editor-actions">
                <button type="button" class="ghost" onclick={() => (editing = null)}>Отмена</button>
                <button type="button" class="primary" disabled={busy} onclick={saveAccess}>
                  Сохранить доступ
                </button>
              </div>
            </div>
          {/if}
        {/each}
      </div>
    {/if}

    <form class="add" onsubmit={add}>
      <input placeholder="новый пользователь" bind:value={newName} maxlength="32" required />
      <select bind:value={newRole}>
        <option value="viewer">просмотр</option>
        <option value="admin">администратор</option>
      </select>
      <button type="submit" class="primary" disabled={busy}>Добавить</button>
    </form>

    <div class="err">{err}</div>

    <div class="actions">
      <button type="button" class="ghost danger" disabled={busy} onclick={killAllSessions}>
        Завершить все сессии
      </button>
      <button type="button" class="ghost" onclick={onclose}>Закрыть</button>
    </div>
  </div>
</div>

<style>
  .bg {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(0,0,0,.45);
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
  }
  .modal {
    width: min(620px, calc(100vw - 32px));
    max-height: calc(100dvh - 48px);
    overflow: auto;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 24px;
    display: flex; flex-direction: column; gap: 14px;
  }
  h2 { font-size: 17px; font-weight: 600; letter-spacing: -.01em; margin: 0; }
  .muted { color: var(--fg-muted); margin: 0; font-size: 13px; }

  .issued {
    border: 1px solid var(--accent);
    border-radius: 10px;
    padding: 12px 14px;
    display: flex; flex-direction: column; gap: 8px;
  }
  .issued-head { font-size: 12px; color: var(--fg-muted); font-weight: 500; }
  .issued code {
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 16px; letter-spacing: .04em;
    user-select: all; word-break: break-all;
  }
  .issued-note { font-size: 12px; color: var(--fg-muted); line-height: 1.4; }
  .issued-actions { display: flex; gap: 8px; justify-content: flex-end; }

  .list { display: flex; flex-direction: column; gap: 2px; }
  .row {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 8px 10px; border-radius: 8px;
  }
  .row:hover { background: rgba(127,127,127,.08); }
  .who { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; min-width: 0; }
  .name { font-size: 14px; font-weight: 500; }
  .role { font-size: 11px; color: var(--fg-muted); }
  .role.admin { color: var(--accent); }
  .access { font-size: 11px; color: var(--fg-muted); opacity: .8; }
  .flag {
    font-size: 10px; padding: 1px 6px; border-radius: 999px;
    background: rgba(127,127,127,.15); color: var(--fg-muted);
  }
  .flag.me { background: rgba(0,113,227,.15); color: var(--accent); }
  .row-actions { display: flex; gap: 4px; flex-shrink: 0; }
  .row-actions button { font-size: 12px; padding: 5px 8px; }

  .access-editor {
    margin: 0 10px 8px;
    padding: 12px;
    border: 1px solid var(--border);
    border-radius: 10px;
    display: flex; flex-direction: column; gap: 8px;
  }
  .check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--fg); }
  .check.strong { font-weight: 500; }
  .check em { color: var(--fg-muted); font-style: normal; font-size: 11px; }
  .nvr-block { display: flex; flex-direction: column; gap: 6px; }
  .nvr-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .cams {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 4px 10px; padding-left: 22px;
  }
  .check.cam { font-size: 12px; color: var(--fg-muted); }
  .check.cam.covered { opacity: .5; }
  button.link {
    font-size: 11px; color: var(--accent); padding: 2px 4px;
  }
  .editor-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 4px; }

  .add { display: flex; gap: 8px; }
  .add input { flex: 1; min-width: 0; }
  input, select {
    padding: 8px 10px; border-radius: 8px;
    border: 1px solid var(--border); background: var(--bg-elev); color: var(--fg);
  }
  input:focus, select:focus { outline: 2px solid var(--accent); outline-offset: -1px; border-color: var(--accent); }

  .err { color: var(--bad); font-size: 13px; min-height: 1.2em; }
  .actions { display: flex; gap: 10px; justify-content: space-between; }
  button.primary { background: var(--accent); color: #fff; padding: 8px 14px; border-radius: 8px; font-weight: 500; }
  button.primary:hover { filter: brightness(1.08); }
  button.primary:disabled { opacity: .6; }
  button.ghost { color: var(--fg-muted); padding: 8px 14px; border-radius: 8px; font-weight: 500; }
  button.ghost:hover { color: var(--fg); }
  button.ghost.danger { color: var(--bad); }
  button.ghost:disabled { opacity: .5; }
</style>
